import type {
  PortalClubwiseDetail,
  PortalComparisonMode,
  PortalCoverage,
  PortalCrmBlock,
  PortalCrmClub,
  PortalCrmCostPerDigitalLead,
  PortalCrmCostPerSale,
  PortalCrmLeadBasis,
  PortalCrmSeriesPoint,
  PortalCrmSourceRow,
  PortalCrmSources,
  PortalCrmTotals,
  PortalSeriesPoint,
} from '../../types/portalReport.js';
import { leadBasisNote, monthToDateLine, type LeadWindow } from '../clubwise/window.js';
import { buildChannelSplits, type ChannelSplitRow } from '../clubwise/channelSplit.js';
import { buildClubwiseBlock, staffLeadsByMonth, type ClubwiseMonthChannelRow } from '../clubwise/report.js';
import { buildCoverage, type AccountFirstDate } from './coverage.js';
import { PORTAL_COMPARISON_PHRASE, portalComparisonRange } from './comparison.js';
import {
  bucketBounds,
  bucketCoverage,
  dayLabel,
  enumerateDays,
  enumerateMonths,
  monthKeyOf,
  monthLabel,
  round2,
  thirteenMonthRange,
} from './series.js';

// CLIENT PORTAL — the CRM block's pure core. No I/O, no clock.
//
// ⚠️⚠️ THE CONSTRAINT EVERYTHING HERE IS SHAPED BY: a sale row in GymSales
// carries no source, no contact_type and no person_id (measured: 188,726 of
// 188,726 blank in all three). So sales cannot be attributed to a channel, and
// nothing in this file may offer a shape that implies they can. See the header
// of server/modules/crmReporting/query/crmQuery.ts for the measurements.
//
// The two derived figures both follow the report's existing null rules, and both
// are the flattering-if-wrong kind:
//
//   · CONVERSION RATE with no leads is NULL, not 0%. Nobody enquired, so nothing
//     converted or failed to; a plotted 0% reads as "everyone who walked in was
//     turned away".
//   · COST PER SALE with no sales is NULL, not $0. Money spent and nothing
//     returned is the opposite of free — the same rule, and the same reasoning,
//     as `deriveCostPerLead`.

/**
 * Sales ÷ all CRM leads, as a percentage to one decimal.
 *
 * ⚠️⚠️ THE DENOMINATOR IS EVERY ENQUIRY THE CLUB RECORDED, from every source —
 * walk-ins, referrals and phone calls included, not only the ads. That is Scott's
 * call and it is the only honest reading available, because sales carry no
 * channel. It makes this a CLUB-LEVEL rate, and the panel has to say so in words:
 * a franchisee reading it as an ad conversion rate would be reading a much worse
 * number than their ads actually produced.
 *
 * ⚠️ NULL, never 0, when there were no leads. See the file header.
 */
export function deriveConversionRate(sales: number, leads: number): number | null {
  if (leads <= 0) return null;
  return Math.round((sales / leads) * 1000) / 10;
}

/**
 * Media spend ÷ sales.
 *
 * ⚠️⚠️ ALL MEDIA OVER ALL SALES, INCLUDING WALK-INS AND REFERRALS. There is no
 * subset of sales this could divide instead, because a sale row has no channel
 * and no source. The brief is explicit that if that caveat cannot be fitted on
 * screen the metric does not ship — an uncaveated cost per sale is a claim we
 * cannot support, and it is the number a franchisee is most likely to quote back.
 *
 * ⚠️ NULL, never 0, when there were no sales.
 */
export function deriveCostPerSale(spend: number, sales: number): number | null {
  if (sales <= 0) return null;
  return round2(spend / sales);
}

/** One club's figures, as they come back from the query layer. */
export interface CrmClubTotals {
  /** The club's id in the CRM, as a string. */
  crmId: string;
  leads: number;
  digitalLeads: number;
  sales: number;
}

/**
 * Sum the per-club rows into the panel's four figures.
 *
 * ⚠️ The rate is RE-DERIVED from the summed numerator over the summed
 * denominator, never averaged across clubs — the same rule every ratio on this
 * report follows, and the one that gets the wrong answer whenever club sizes
 * differ (i.e. always).
 */
export function buildCrmTotals(
  rows: readonly CrmClubTotals[],
): PortalCrmTotals & { leads: number; digitalLeads: number } {
  let leads = 0;
  let digitalLeads = 0;
  let sales = 0;
  for (const r of rows) {
    leads += r.leads;
    digitalLeads += r.digitalLeads;
    sales += r.sales;
  }
  return {
    leads: round2(leads),
    digitalLeads: round2(digitalLeads),
    sales: round2(sales),
    conversionRate: deriveConversionRate(sales, leads),
  };
}

/**
 * Cost per sale, per currency.
 *
 * ⚠️ PER-CURRENCY AND NEVER BLENDED, like every other money figure a client
 * sees. The SALES count is currency-agnostic (a sale is a sale whoever paid for
 * the advertising), so each currency's spend divides by the same total — which
 * is the only reading available when a scope genuinely spans two currencies, and
 * one more reason the caveat beside it is not optional.
 *
 * ⚠️ Spend arrives from the ad report's COMBINED totals, so this figure changes
 * with the report's own channel suppression: a client whose report does not
 * mention Google is not shown Google's spend in this ratio either. That is
 * consistent by construction rather than by a second rule here.
 */
export function buildCrmCostPerSale(
  spendByCurrency: readonly { currency: string; spend: number }[],
  sales: number,
): PortalCrmCostPerSale[] {
  const acc = new Map<string, number>();
  for (const s of spendByCurrency) {
    acc.set(s.currency, (acc.get(s.currency) ?? 0) + s.spend);
  }
  return [...acc.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([currency, spend]) => ({
      currency,
      spend: round2(spend),
      costPerSale: deriveCostPerSale(spend, sales),
    }));
}

/** An in-scope account, as the assembly knows it before any query runs. */
export interface CrmScopeAccount {
  accountId: string;
  name: string;
  /** Null when the account has no CRM link, or its brand has no CRM table. */
  crmId: string | null;
}

/**
 * Split the scope into the clubs these figures cover and the clubs they do not.
 *
 * ⚠️⚠️ THIS IS RULE 15 AT SCOPE LEVEL. A club with no CRM link contributes
 * nothing to a SUM, and a sum that quietly includes it is presenting an
 * unmeasured club as a club that took no enquiries and made no sales — with a
 * conversion rate computed over the pair. On a group report that silently drags
 * the rate down and there is nothing on screen to explain it.
 *
 * ⚠️ A CLUB WITH A CRM ID BUT NO HISTORY AT ALL IS ALSO UNCOUNTED. That is not a
 * quiet month: `firstDates` comes from an UNBOUNDED MIN(date) over the whole
 * table, so no first date means the id matches no row that has ever existed —
 * a mis-set id, which is a different fact from zero enquiries and must not be
 * dressed as one. A club WITH history and no rows in the period IS counted; that
 * is a real zero and saying so is accurate.
 */
export function splitCrmClubs(
  accounts: readonly CrmScopeAccount[],
  firstDates: readonly AccountFirstDate[],
): { counted: PortalCrmClub[]; uncounted: PortalCrmClub[] } {
  const withHistory = new Set(firstDates.filter((d) => !!d.firstDate).map((d) => d.accountId));
  const counted: PortalCrmClub[] = [];
  const uncounted: PortalCrmClub[] = [];
  for (const a of accounts) {
    const club = { accountId: a.accountId, name: a.name };
    if (a.crmId && withHistory.has(a.accountId)) counted.push(club);
    else uncounted.push(club);
  }
  const byName = (x: PortalCrmClub, y: PortalCrmClub): number => x.name.localeCompare(y.name);
  return { counted: counted.sort(byName), uncounted: uncounted.sort(byName) };
}

export interface BuildCrmBlockInput {
  /** The footer's name for the CRM; absent = GymSales (and the block carries no `sourceLabel`). */
  sourceLabel?: string | null;
  accounts: readonly CrmScopeAccount[];
  /** Per-club period totals, keyed by CRM id. */
  rows: readonly CrmClubTotals[];
  /** Per-account first CRM date — unbounded, not period-scoped. */
  firstDates: readonly AccountFirstDate[];
  /** The combined ad spend these sales are divided by, per currency. */
  spendByCurrency: readonly { currency: string; spend: number }[];
  /** When the CRM feed last landed. Its OWN date. */
  asOf: string | null;
  /** The selected period, bucketed as the ad series is. */
  periodSeries: readonly PortalCrmSeriesPoint[];
  /** The twelve rolling months. */
  monthlySeries: readonly PortalCrmSeriesPoint[];
  /**
   * Per-club totals over the immediately preceding period.
   *
   * ⚠️ Only becomes a `previous` when the CRM COVERS that period — see
   * `crmComparabilityReason`. Rows without coverage are a partial count, and a
   * partial count in a delta is worse than no delta.
   */
  previousRows: readonly CrmClubTotals[];
  /**
   * ⚠️⚠️ THE WINDOW **AND** THE BASIS, as one unforgeable value. `previousRows`
   * was read over `.range` and the caller filters last year's AD SPEND to the
   * same `.range`; taking a bare range here would let those two drift, which is
   * the silent cost-per-sale defect `CrmComparison` exists to prevent.
   */
  previousComparison: CrmComparison;
  sourceRows: readonly PortalCrmSourceRow[];
  /**
   * ⚠️⚠️ LAST YEAR'S AD SPEND, per currency — or NULL when the ad channels did
   * not cover that window.
   *
   * A cost delta needs BOTH halves: last year's CRM count AND last year's spend.
   * This is the cross-source trap one window further back, and on a real club it
   * is the half that is missing — ad history typically starts long after the CRM
   * does, so the counts compare and the costs do not. That is correct, and the
   * band has to make it look deliberate rather than like three broken tiles.
   */
  previousSpendByCurrency: readonly { currency: string; spend: number }[] | null;
  /** Why last year's spend is unusable, when it is. */
  previousSpendUnavailableReason: string | null;
  /**
   * ⚠️⚠️ MONTHLY-GRAIN LEAD SOURCES ONLY (ClubWise), else null/absent.
   *
   * The lead window and the per-club totals over EXACTLY that window's months.
   * When present: lead and digital-lead totals come from `windowRows` (never from
   * `rows`, whose leads are a date-range sum that drops a month whose 1st falls
   * outside the range); the conversion rate divides the SAME window's sales by
   * its leads; the comparison carries no lead figures; and cost per internet lead
   * is not computed (its spend would be over a different window than its leads).
   */
  monthlyLeads?: {
    window: LeadWindow;
    windowRows: readonly CrmClubTotals[];
    /** Per-club totals over the previous equivalent whole months; null unless all were uploaded. */
    previousWindowRows?: readonly CrmClubTotals[] | null;
    /** (month × channel) lead aggregates, for the ClubWise block. */
    detail?: readonly ClubwiseMonthChannelRow[];
    /** The ClubWise data tab's tables (assembled by the server read). */
    tabDetail?: PortalClubwiseDetail | null;
    /** (channel x source x contact type) aggregates for the channel table's expand. */
    channelSplitRows?: readonly ChannelSplitRow[];
  } | null;
  /**
   * ⚠️⚠️ MONTHLY-GRAIN ONLY. Ad spend over EXACTLY the lead window's whole months
   * (`current`) and the previous equivalent months (`previous`) — NULL when the ad
   * channels do not cover every one of them. Cost per digital lead divides THIS by
   * the digital leads of the same months, never the raw date range's spend.
   */
  windowSpend?: {
    current: readonly { currency: string; spend: number }[] | null;
    previous: readonly { currency: string; spend: number }[] | null;
  } | null;
}

/**
 * Assemble the CRM block.
 *
 * ⚠️⚠️ IT RETURNS NULL WHEN NO CLUB IN SCOPE CAN BE COUNTED, and the panel is
 * then OMITTED rather than emptied. That is the omit-a-section-with-no-source
 * half of the report's absence rule: there is nothing to explain, and a
 * placeholder would promise a feature that is not connected for this client. A
 * scope where SOME clubs count keeps the panel and names the rest.
 *
 * ⚠️ Only rows belonging to a COUNTED club are summed. A row can only arrive for
 * a club whose id was in the query, but reading the totals off the counted set
 * rather than off the query result is what keeps the figures and the "counts N of
 * M locations" sentence describing the same thing.
 */
export function buildCrmBlock(input: BuildCrmBlockInput): PortalCrmBlock | null {
  const { counted, uncounted } = splitCrmClubs(input.accounts, input.firstDates);
  if (counted.length === 0) return null;

  const countedCrmIds = new Set(
    input.accounts
      .filter((a) => counted.some((c) => c.accountId === a.accountId))
      .map((a) => a.crmId)
      .filter((id): id is string => !!id),
  );
  const rows = input.rows.filter((r) => countedCrmIds.has(r.crmId));
  const plainTotals = buildCrmTotals(rows);

  // ⚠️⚠️ MONTHLY-GRAIN LEADS (ClubWise): leads, digital leads and the conversion
  // rate come from the whole-month window; sales stay the selected range's.
  const ml = input.monthlyLeads ?? null;
  const hasWindow = ml !== null && ml.window.measuredMonths.length > 0;
  let totals: PortalCrmTotals = plainTotals;
  let leadBasis: PortalCrmLeadBasis | null = null;
  if (ml) {
    const w = buildCrmTotals(ml.windowRows.filter((r) => countedCrmIds.has(r.crmId)));
    totals = {
      leads: hasWindow ? w.leads : null,
      digitalLeads: hasWindow ? w.digitalLeads : null,
      sales: plainTotals.sales,
      // ⚠️ THE SAME WINDOW FOR BOTH HALVES: w.sales and w.leads cover the same
      // months. Dividing the selected range's sales by whole-month leads would
      // be a rate over two different populations.
      conversionRate: hasWindow ? deriveConversionRate(w.sales, w.leads) : null,
    };
    leadBasis = {
      grain: 'monthly',
      measuredMonths: ml.window.measuredMonths,
      notUploadedMonths: ml.window.notUploadedMonths,
      monthToDate: ml.window.monthToDate,
      note: leadBasisNote(ml.window),
      monthToDateNote: monthToDateLine(ml.window),
    };
  }

  // ⚠️ Coverage is measured over the COUNTED clubs only. An uncounted club has no
  // coverage question to answer, and letting it push `fullFromDate` forward would
  // flag months that are genuinely complete for every club actually being
  // reported — the same reasoning `buildCoverage` already documents for accounts
  // with no data on a channel.
  const coverage: PortalCoverage = buildCoverage(
    counted.map((c) => c.accountId),
    input.firstDates,
  );

  // ⚠️ BOTH SIDES MEASURED OR NO DELTA. A club whose CRM starts inside the
  // comparison window would otherwise show a spurious +100%.
  const previousUnavailableReason = crmComparabilityReason(
    input.previousComparison.range,
    coverage,
    input.previousComparison.mode,
  );
  const previousPlain =
    previousUnavailableReason === null
      ? buildCrmTotals(input.previousRows.filter((r) => countedCrmIds.has(r.crmId)))
      : null;
  // ⚠️ A monthly-grain source's previous period carries SALES ONLY: its lead
  // sum is a date-range sum that drops a month whose 1st falls outside the
  // window, so a lead comparison would be a comparison of two wrong numbers.
  // ⚠️ CLUBWISE: whole months against the PREVIOUS EQUIVALENT whole months, and
  // only when every one of those was uploaded (the rows are null otherwise). The
  // sales delta keeps the selected range's own figures, as before.
  const prevW =
    ml?.previousWindowRows != null
      ? buildCrmTotals(ml.previousWindowRows.filter((r) => countedCrmIds.has(r.crmId)))
      : null;
  const previous: PortalCrmTotals | null =
    previousPlain !== null && ml
      ? {
          leads: prevW ? prevW.leads : null,
          digitalLeads: prevW ? prevW.digitalLeads : null,
          sales: previousPlain.sales,
          conversionRate: prevW ? deriveConversionRate(prevW.sales, prevW.leads) : null,
        }
      : previousPlain;

  // ⚠️ BOTH HALVES OR NEITHER, for the two cost figures and total spend. The CRM
  // side can be comparable while the ad side is not; a delta computed from one
  // real half and one absent one is the +100% this rule exists to refuse.
  const previousSpend =
    previousUnavailableReason === null && input.previousSpendUnavailableReason === null
      ? (input.previousSpendByCurrency ?? null)
      : null;

  return {
    totals,
    costPerSale: buildCrmCostPerSale(input.spendByCurrency, totals.sales),
    // ⚠️ NULL under a monthly-grain source: its leads are over whole months and
    // its spend is over the selected range — two windows. Honest dash, not a ratio.
    costPerDigitalLead:
      ml && hasWindow && input.windowSpend?.current
        ? // ⚠️ SAME WHOLE MONTHS for the spend and the leads — never the raw range.
          buildCrmCostPerDigitalLead(input.windowSpend.current, totals.digitalLeads)
        : buildCrmCostPerDigitalLead(input.spendByCurrency, ml ? null : totals.digitalLeads),
    spendByCurrency: [...input.spendByCurrency],
    previousSpendByCurrency: previousSpend === null ? null : [...previousSpend],
    previousCostPerSale:
      previousSpend === null || previous === null
        ? null
        : buildCrmCostPerSale(previousSpend, previous.sales),
    previousCostPerDigitalLead: ml
      ? prevW && previous && input.windowSpend?.previous
        ? buildCrmCostPerDigitalLead(input.windowSpend.previous, prevW.digitalLeads)
        : null
      : previousSpend === null || previous === null
        ? null
        : buildCrmCostPerDigitalLead(previousSpend, previous.digitalLeads),
    coverage,
    asOf: input.asOf,
    ...(input.sourceLabel ? { sourceLabel: input.sourceLabel } : {}),
    countedClubs: counted,
    uncountedClubs: uncounted,
    periodSeries: [...input.periodSeries],
    monthlySeries: [...input.monthlySeries],
    previous,
    previousUnavailableReason,
    // ⚠️⚠️ BOTH WIRE FIELDS COME OFF THE ONE PAIR, so they cannot name a
    // different window from the one the figures were computed over. They are
    // PLAIN on the wire because a brand is a compile-time fiction — it does not
    // survive JSON — and the client only reads them to write a sentence.
    previousRange: { from: input.previousComparison.range.from, to: input.previousComparison.range.to },
    previousComparisonMode: input.previousComparison.mode,
    sources: buildCrmSources(input.sourceRows),
    leadBasis,
    // ⚠️ OMITTED (not null) for GymSales, so their payload is unchanged.
    ...(ml && ml.detail
      ? {
          clubwise: {
            ...buildClubwiseBlock({
              window: ml.window,
              rows: ml.detail,
              mode: input.previousComparison.mode,
              previousSales: prevW?.sales ?? 0,
            }),
            ...(ml.tabDetail ? { detail: ml.tabDetail } : {}),
            ...(ml.channelSplitRows && ml.channelSplitRows.length > 0
              ? { channelSplits: buildChannelSplits(ml.channelSplitRows) }
              : {}),
          },
        }
      : {}),
  };
}


// ---------------------------------------------------------------------------
// ⚠️⚠️ THE CRM'S OWN SERIES
// ---------------------------------------------------------------------------

/** One measured bucket as it comes back from the query layer. */
export interface CrmMeasuredBucket {
  bucket: string;
  leads: number;
  digitalLeads: number;
  sales: number;
}

export interface BuildCrmSeriesOptions {
  from: string;
  to: string;
  grouping: 'daily' | 'monthly';
  measured: readonly CrmMeasuredBucket[];
  coverage: PortalCoverage;
  today: string;
  /**
   * ⚠️⚠️ MONTHLY-GRAIN LEAD SOURCES ONLY (ClubWise). `uploadedMonths` is the set
   * of `YYYY-MM` months uploaded for every club. Under it:
   *   · a bucket whose month was NOT uploaded is `no-data` with null counts —
   *     leads AND sales, since both only exist because of an upload — never zero;
   *   · on a DAILY grouping, leads and digital leads are NULL (a gap): a lead has
   *     no day, and putting the month's leads on the 1st is a fake spike. Sales
   *     keep their real day.
   */
  monthly?: {
    uploadedMonths: ReadonlySet<string>;
    /** month (YYYY-MM) → the day its upload runs to, for months part-way through. */
    partialTo?: ReadonlyMap<string, string>;
  } | null;
  /**
   * ⚠️ CLUBWISE ONLY: staff generated leads per uploaded month. When given, every
   * point carries `staffLeads` — null for a month not uploaded and for any daily
   * bucket (a lead has no day). Absent = the property is not set at all.
   */
  staffByMonth?: ReadonlyMap<string, number | null> | null;
}

/**
 * The CRM's counts as a continuous run of buckets.
 *
 * ⚠️⚠️ IT REUSES `bucketCoverage` RATHER THAN DECIDING COVERAGE ITSELF. That
 * function is where "was this bucket measured" is defined for the whole report,
 * and a second answer to it here is how a CRM chart would start drawing zeros
 * over months the ad charts correctly leave as gaps.
 *
 * ⚠️ CONTINUOUS: every bucket in the range appears, so a chart cannot skip a
 * period. An unmeasured one carries NULLS on all three counts — never zero. A
 * MEASURED bucket with no rows is a real zero, which is a different fact and is
 * how a quiet month reads.
 */
export function buildCrmSeries(opts: BuildCrmSeriesOptions): PortalCrmSeriesPoint[] {
  const points = buildCrmSeriesBase(opts);
  const staff = opts.staffByMonth;
  if (!staff) return points;
  return points.map((p) => ({
    ...p,
    staffLeads:
      opts.grouping === 'daily' || p.leads === null ? null : (staff.get(p.bucket.slice(0, 7)) ?? null),
  }));
}

export { staffLeadsByMonth };

function buildCrmSeriesBase(opts: BuildCrmSeriesOptions): PortalCrmSeriesPoint[] {
  const keys =
    opts.grouping === 'daily'
      ? enumerateDays(opts.from, opts.to)
      : enumerateMonths(opts.from, opts.to);

  const byKey = new Map<string, CrmMeasuredBucket>();
  for (const row of opts.measured) {
    const existing = byKey.get(row.bucket);
    if (existing) {
      existing.leads += row.leads;
      existing.digitalLeads += row.digitalLeads;
      existing.sales += row.sales;
    } else {
      byKey.set(row.bucket, { ...row });
    }
  }

  return keys.map((key) => {
    const label = opts.grouping === 'daily' ? dayLabel(key) : monthLabel(key);
    // ⚠️ Through `bucketBounds`, exactly as `buildSeries` does — the in-progress
    // and partial rules key off the bucket's LAST day, not its name.
    const bounds = bucketBounds(key);
    const coverage = bucketCoverage(bounds.start, bounds.end, opts.coverage, opts.today);
    if (coverage === 'no-data') {
      return { bucket: key, label, coverage, leads: null, digitalLeads: null, sales: null };
    }
    // ⚠️ MONTHLY GRAIN: a month nobody uploaded is NOT MEASURED, whatever the
    // club's first date says (a gap in the middle of a run is invisible to the
    // first-date coverage rule).
    if (opts.monthly && !opts.monthly.uploadedMonths.has(key.slice(0, 7))) {
      return { bucket: key, label, coverage: 'no-data', leads: null, digitalLeads: null, sales: null };
    }
    let leads = 0;
    let digitalLeads = 0;
    let sales = 0;
    if (opts.grouping === 'daily') {
      const row = byKey.get(key);
      if (row) {
        leads = row.leads;
        digitalLeads = row.digitalLeads;
        sales = row.sales;
      }
    } else {
      for (const [k, row] of byKey) {
        // ⚠️ The query already buckets monthly, so `k` IS the month key — but a
        // daily row reaching a monthly series would otherwise be dropped
        // silently, and `monthKeyOf` makes both shapes land in the right month.
        if (monthKeyOf(k) !== key) continue;
        leads += row.leads;
        digitalLeads += row.digitalLeads;
        sales += row.sales;
      }
    }
    // ⚠️ DAILY GROUPING + MONTHLY LEADS: no lead has a day, so leads are a gap.
    const leadsHaveNoDay = !!opts.monthly && opts.grouping === 'daily';
    const partialTo = opts.grouping === 'monthly' ? opts.monthly?.partialTo?.get(key.slice(0, 7)) : undefined;
    return {
      bucket: key,
      label,
      coverage,
      leads: leadsHaveNoDay ? null : round2(leads),
      digitalLeads: leadsHaveNoDay ? null : round2(digitalLeads),
      sales: round2(sales),
      ...(partialTo ? { partialTo } : {}),
    };
  });
}

// ---------------------------------------------------------------------------
// ⚠️⚠️ THE CROSS-SOURCE RATIOS — the first on this report
// ---------------------------------------------------------------------------

/** Every metric a CRM chart can plot. */
export type CrmMetricKey =
  | 'crmLeads'
  | 'crmDigitalLeads'
  | 'crmStaffLeads'
  | 'crmSales'
  | 'crmCloseRate'
  | 'crmCostPerDigitalLead'
  | 'crmCostPerSale';

/**
 * ⚠️ EVERY key, as a value. `CrmMetricKey` is a union and TypeScript cannot
 * enumerate one at runtime, so this list is what lets a guard exist at all — and
 * `satisfies` is what makes a missing entry a compile error rather than a metric
 * that silently stops being recognised as CRM.
 */
export const CRM_METRIC_KEYS = [
  'crmLeads',
  'crmDigitalLeads',
  'crmStaffLeads',
  'crmSales',
  'crmCloseRate',
  'crmCostPerDigitalLead',
  'crmCostPerSale',
] as const satisfies readonly CrmMetricKey[];

const CRM_METRIC_SET: ReadonlySet<string> = new Set(CRM_METRIC_KEYS);

/**
 * ⚠️ A TYPE GUARD, not a cast. The chart card branches on it to decide which
 * value reader applies, and a cast there would let an ad metric reach the CRM
 * reader — where it would return `undefined` and plot as a blank line rather
 * than failing.
 */
export function isCrmMetric(metric: string): metric is CrmMetricKey {
  return CRM_METRIC_SET.has(metric);
}

/** The three that need AD SPEND as well as a CRM count. */
export const CRM_COST_METRICS: ReadonlySet<CrmMetricKey> = new Set<CrmMetricKey>([
  'crmCostPerDigitalLead',
  'crmCostPerSale',
]);

/**
 * ONE bucket's value for a CRM metric.
 *
 * ⚠️⚠️ THE TWO COST METRICS DIVIDE AD SPEND BY A CRM COUNT, and that is the
 * first cross-source ratio on this report. Everything else here lives inside one
 * source (Meta's spend over Meta's leads), so the rules below are new:
 *
 *   ⚠️ IF EITHER SIDE IS UNMEASURED THE RATIO IS NULL. Not zero, and not
 *     computed against whichever side we happen to have. A month where the CRM
 *     has data and the ad channels do not is not a cheap month; it is a month we
 *     cannot price. `spend === null` is exactly as disqualifying as a null sale
 *     count.
 *
 *   ⚠️ AND A DENOMINATOR OF ZERO IS STILL NULL — spend with no sales has no cost
 *     per sale, and a $0.00 reads as "the sales were free", which is the
 *     flattering direction to be wrong in.
 */
export function crmValueAt(
  point: PortalCrmSeriesPoint,
  metric: CrmMetricKey,
  spend: number | null,
): number | null {
  switch (metric) {
    case 'crmLeads':
      return point.leads;
    case 'crmDigitalLeads':
      return point.digitalLeads;
    case 'crmStaffLeads':
      // ⚠️ Absent (GymSales) and null (month not uploaded) are both "not measured".
      return point.staffLeads ?? null;
    case 'crmSales':
      return point.sales;
    case 'crmCloseRate':
      return point.sales === null || point.leads === null
        ? null
        : deriveConversionRate(point.sales, point.leads);
    case 'crmCostPerDigitalLead':
      return spend === null || point.digitalLeads === null
        ? null
        : deriveCostPerSale(spend, point.digitalLeads);
    case 'crmCostPerSale':
      return spend === null || point.sales === null ? null : deriveCostPerSale(spend, point.sales);
  }
}

/**
 * The ad spend to divide by, per CRM bucket.
 *
 * ⚠️⚠️ ALIGNED BY BUCKET KEY, NEVER BY ARRAY POSITION. Two sources, two queries:
 * positional alignment silently shifts a month the day one of them omits an
 * empty bucket, and the result is a cost per sale computed from the wrong
 * month's spend — a plausible number that is simply about a different month.
 *
 * ⚠️ An ad bucket that is UNMEASURED contributes NULL rather than being missing,
 * so the caller cannot mistake "we did not measure the spend" for "there was no
 * spend". `spend === null` on a measured-with-no-spend bucket does not arise:
 * `buildSeries` gives a measured bucket a real 0.
 */
export function spendByBucket(
  adSeries: readonly PortalSeriesPoint[],
): ReadonlyMap<string, number | null> {
  const out = new Map<string, number | null>();
  for (const p of adSeries) {
    out.set(p.bucket, p.coverage === 'no-data' ? null : p.spend);
  }
  return out;
}

/**
 * One CRM metric over a run of buckets, joined to the ad spend by key.
 *
 * ⚠️ A CRM bucket with NO matching ad bucket gets `null` spend — an absent key
 * is not a zero-spend month, it is a month the ad series does not describe.
 */
export function crmSeriesValues(
  series: readonly PortalCrmSeriesPoint[],
  metric: CrmMetricKey,
  spend: ReadonlyMap<string, number | null>,
): Array<number | null> {
  return series.map((p) =>
    crmValueAt(p, metric, CRM_COST_METRICS.has(metric) ? (spend.get(p.bucket) ?? null) : null),
  );
}

// ---------------------------------------------------------------------------
// Cost per internet lead, and the comparison
// ---------------------------------------------------------------------------

/**
 * Media spend ÷ internet leads, per currency.
 *
 * ⚠️⚠️ THE DEFENSIBLE COST FIGURE, and the reason it leads. Internet leads are
 * at least plausibly ad-driven; total enquiries include walk-ins and referrals,
 * so spend over every enquiry flatters us with leads the advertising never
 * touched. Neither is an ad cost per lead — the report already carries that,
 * from the ad platforms' own attribution — and nothing here may present them as
 * comparable.
 *
 * ⚠️ NULL with no internet leads. Same rule as everything else here.
 */
export function buildCrmCostPerDigitalLead(
  spendByCurrency: readonly { currency: string; spend: number }[],
  /** ⚠️ NULL = not measured (monthly-grain source, no whole uploaded month): every cost is null. */
  digitalLeads: number | null,
): PortalCrmCostPerDigitalLead[] {
  const acc = new Map<string, number>();
  for (const s of spendByCurrency) {
    acc.set(s.currency, (acc.get(s.currency) ?? 0) + s.spend);
  }
  return [...acc.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([currency, spend]) => ({
      currency,
      spend: round2(spend),
      costPerDigitalLead: digitalLeads === null ? null : deriveCostPerSale(spend, digitalLeads),
    }));
}

/**
 * ⚠️⚠️ THE CHART WINDOW — the current month plus the twelve before it, which is
 * EXACTLY `thirteenMonthRange`, the window every other rolling chart on this
 * report already uses.
 *
 * The bug this fixes: the CRM monthly series was built over `yearOnYearRange`,
 * whose own doc comment says "⚠️ 24 months, not 12" because both runs of the
 * year-on-year chart come out of one query. So the CRM charts drew ~24 columns
 * back to Oct 2024 and the x-axis collapsed into `Oct 2024 2024 2024 2025 2025
 * …`, one tick per bucket with the month names lost.
 *
 * ⚠️⚠️ THIRTEEN IS ONLY SAFE BECAUSE THE AD SIDE IS THIRTEEN TOO. The two CRM
 * cost charts divide AD SPEND by a CRM count and join BY BUCKET KEY, so a CRM
 * bucket with no ad partner resolves to a null cost and the chart shades a NOT
 * MEASURED band over a month whose spend we have — a spurious "we did not
 * measure this", which is the class of misstatement this report exists to
 * prevent. The band therefore feeds the cards `block.thirteenMonthSeries`
 * (13 monthly keys, already fetched) rather than `yearOnYear.current` (12).
 * ⚠️ If either side is ever changed, change both, or the join silently loses a
 * month at one end.
 *
 * ⚠️ The FETCH stays at 24 months — the year-on-year comparison needs it, and it
 * is one query either way.
 */
export function crmMonthlyRange(today: string): { from: string; to: string } {
  return thirteenMonthRange(today);
}

/**
 * Nominal brand. Declared, never exported — a comparison pair cannot be forged.
 *
 * ⚠️ The device `PortalScope` and `CostLayer` use, and for the same reason: the
 * type is the enforcement. A `declare`d unique symbol has NO RUNTIME VALUE, so
 * the object carries no extra key and the CAST inside `resolveCrmComparison` is
 * what confers the brand.
 */
declare const CRM_COMPARISON_BRAND: unique symbol;

/**
 * ⚠️⚠️ THE CRM BAND'S COMPARISON WINDOW, INSEPARABLE FROM THE BASIS IT CAME
 * FROM — and the only way to get one is `resolveCrmComparison`.
 *
 * The band's COUNTS come from a CRM query over this window; its COST figures
 * divide AD SPEND filtered to the same window. That is two reads, in two
 * modules, one of them a separate service. ⚠️⚠️ IF THOSE TWO WINDOWS EVER
 * DISAGREE, cost per sale becomes this period's sale count over LAST YEAR'S
 * spend: a plausible, ordinary-looking, wrong figure that every existing guard
 * passes — on the number most likely to be quoted back at Kyck in a renewal.
 *
 * So the pair travels as ONE value: one resolver derives the range FROM the
 * mode, and both readers take `.range` off the same object. A hand-built
 * `{ mode, range }` does not typecheck, which is what makes "the spend follows
 * the counts" a property of the types rather than a rule to remember.
 *
 * ⚠️ A runtime `if` is the weaker version — it can be deleted by one edit and
 * nothing fails until a client changes their comparison.
 */
export interface CrmComparison {
  readonly [CRM_COMPARISON_BRAND]: true;
  readonly mode: PortalComparisonMode;
  readonly range: { readonly from: string; readonly to: string };
}

/**
 * Resolve the CRM band's comparison window from the client's own selection.
 *
 * ⚠️⚠️ THE SELECTOR DRIVES THE BAND. It was pinned to year-on-year
 * UNCONDITIONALLY: Scott asked for year-on-year as the DEFAULT and it was built
 * as the ONLY basis, so a client choosing "vs previous month" watched the ad
 * blocks move while the CRM band went on comparing against last year — and the
 * band's own basis line went on SAYING last year, so the page disagreed with
 * itself in writing.
 *
 * ⚠️ YEAR-ON-YEAR IS STILL THE DEFAULT (an absent mode resolves to it): a gym's
 * enquiries are seasonal, so a January against a December is mostly a fact about
 * the calendar. Scott: *"The default comparison should always be compared to last
 * year."*
 *
 * ⚠️ It delegates to `portalComparisonRange`, which delegates to the AM
 * Advertising Home's own `comparisonRange`. A client and their account manager
 * disagreeing about what "vs last year" means is a credibility problem nobody
 * notices until two reports are compared, so there is one definition.
 */
export function resolveCrmComparison(
  range: { from: string; to: string },
  mode: PortalComparisonMode | undefined,
): CrmComparison {
  const resolved: PortalComparisonMode = mode ?? 'lastYear';
  // ⚠️ THE ONE CAST THAT CONFERS THE BRAND, confined to this expression — the
  // `resolveCostLayer` shape. Every other holder RECEIVES one.
  return {
    mode: resolved,
    range: portalComparisonRange(range, resolved),
  } as CrmComparison;
}

/**
 * Is the comparison period honestly comparable?
 *
 * ⚠️⚠️ COMPLETE OR ABSENT — `PortalYearOnYear`'s contract, reused rather than
 * reinvented. A club whose CRM history starts inside the comparison window would
 * otherwise show a spurious +100%, and CRM history genuinely differs per club
 * (NZ from 2024, AU from 2021).
 *
 * ⚠️⚠️ AND THERE IS STILL NO FALLBACK TO ANOTHER BASIS. Two adjacent tiles
 * carrying deltas on different bases, unlabelled, is worse than one tile with no
 * delta: the reader compares the percentages, not the footnotes. The basis is
 * stated ONCE for the band, which is only honest if every delta on it shares it.
 *
 * ⚠️⚠️ BUT THAT RULE FORBIDS MIXING BASES ON ONE BAND — it never forbade
 * CHOOSING which basis the whole band uses, and reading it as the latter is what
 * pinned the band to year-on-year. So the window is now the client's own
 * selection, and this refusal NAMES THE WINDOW IT IS REFUSING, through the one
 * shared phrase vocabulary: a sentence saying "last year" while the band
 * compares against last month is that disagreement in writing.
 */
export function crmComparabilityReason(
  previousRange: { from: string; to: string },
  coverage: PortalCoverage,
  mode: PortalComparisonMode,
): string | null {
  const window = PORTAL_COMPARISON_PHRASE[mode];
  if (coverage.firstDate === null) {
    return `No comparison: your CRM history does not reach ${window}.`;
  }
  if (coverage.firstDate > previousRange.from) {
    return `No comparison: your CRM history starts partway through ${window}.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Lead sources
// ---------------------------------------------------------------------------

/** How many source rows are shown before the tail is folded. */
export const CRM_SOURCE_ROWS = 8;

/**
 * Rank the sources and fold the long tail.
 *
 * ⚠️⚠️ THE ONLY THING FOLDED IS THE TAIL BELOW THE TOP N. In particular a blank
 * or `Unknown` source keeps its own row however large — measured on Scott's
 * example it is 82 of 262, the SECOND-LARGEST source, and that is a finding (a
 * third of enquiries are not being tagged by club staff) rather than noise.
 *
 * ⚠️ NOTHING IS RENAMED OR MERGED. Two spellings of one thing stay two rows.
 * They are two rows in the CRM, and merging them is a decision nobody has made.
 */
export function buildCrmSources(
  rows: readonly PortalCrmSourceRow[],
  limit = CRM_SOURCE_ROWS,
): PortalCrmSources {
  const sorted = [...rows]
    .filter((r) => r.leads > 0)
    // ⚠️ Ties broken by NAME, so the order is stable across two reads of the
    // same period rather than following whatever order BigQuery returned.
    .sort((a, b) => b.leads - a.leads || a.source.localeCompare(b.source));
  const kept = sorted.slice(0, limit);
  const tail = sorted.slice(limit);
  return {
    rows: kept,
    otherLeads: tail.reduce((sum, r) => sum + r.leads, 0),
    otherSources: tail.length,
    total: sorted.reduce((sum, r) => sum + r.leads, 0),
  };
}
